import React, { useState, useEffect } from 'react';
import { Check, AlertCircle, Loader2, Sparkles } from 'lucide-react';
import Modal from '../ui/Modal';
import { Button } from '../ui';
import { useToast } from '../../contexts/ToastContext';
import {
  subscriptionService,
  SubscriptionTier,
  Subscription,
  UpgradePreview,
} from '../../services/subscriptionService';

interface UpgradeSubscriptionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUpgradeSuccess: () => void;
}

const UpgradeSubscriptionModal: React.FC<UpgradeSubscriptionModalProps> = ({
  isOpen,
  onClose,
  onUpgradeSuccess,
}) => {
  const { showError, showSuccess } = useToast();

  // Data state
  const [tiers, setTiers] = useState<SubscriptionTier[]>([]);
  const [currentSubscription, setCurrentSubscription] = useState<Subscription | null>(null);
  const [preview, setPreview] = useState<UpgradePreview | null>(null);

  // Selection state
  const [selectedTierId, setSelectedTierId] = useState<number | null>(null);
  const [billingCycle, setBillingCycle] = useState<'monthly' | 'annual'>('monthly');

  // Loading states
  const [loadingData, setLoadingData] = useState(true);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [upgrading, setUpgrading] = useState(false);

  // Load tiers and current subscription when modal opens
  useEffect(() => {
    if (!isOpen) return;

    const loadData = async () => {
      setLoadingData(true);
      try {
        const [tiersResponse, subscriptionResponse] = await Promise.all([
          subscriptionService.getSubscriptionTiers(),
          subscriptionService.getCurrentSubscription(),
        ]);

        if (tiersResponse.success) {
          // Filter to only active tiers, excluding Enterprise (manual enrollment only)
          setTiers(
            tiersResponse.data.filter(
              (t) => t.is_active && t.tier_name.toLowerCase() !== 'enterprise'
            )
          );
        }

        if (subscriptionResponse.success && subscriptionResponse.data?.subscription) {
          const sub = subscriptionResponse.data.subscription;
          setCurrentSubscription(sub);
          setBillingCycle(sub.billing_cycle);
        }
      } catch (error) {
        showError('Failed to load subscription data', 'Please try again');
      } finally {
        setLoadingData(false);
      }
    };

    loadData();
  }, [isOpen, showError]);

  // Load preview when tier or billing cycle changes
  useEffect(() => {
    if (!selectedTierId || !currentSubscription) {
      setPreview(null);
      return;
    }

    if (selectedTierId === currentSubscription.subscription_tier_id) {
      setPreview(null);
      return;
    }

    const loadPreview = async () => {
      setLoadingPreview(true);
      try {
        const response = await subscriptionService.previewUpgrade(selectedTierId, billingCycle);
        if (response.success) {
          setPreview(response.data);
        } else {
          setPreview(null);
        }
      } catch (error) {
        setPreview(null);
      } finally {
        setLoadingPreview(false);
      }
    };

    loadPreview();
  }, [selectedTierId, billingCycle, currentSubscription]);

  const handleUpgrade = async () => {
    if (!selectedTierId) return;

    setUpgrading(true);
    try {
      const response = await subscriptionService.upgradeSubscription({
        tierId: selectedTierId,
        billingCycle,
        applyImmediately: true,
      });

      if (response.success) {
        showSuccess(
          'Subscription Upgraded!',
          `You are now on the ${response.data.change.to} plan.`
        );
        onUpgradeSuccess();
        onClose();
      } else {
        showError('Upgrade Failed', response.message || 'Please try again');
      }
    } catch (error) {
      showError('Upgrade Failed', 'An unexpected error occurred. Please try again.');
    } finally {
      setUpgrading(false);
    }
  };

  const formatPrice = (amount: number, currency: string = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const isCurrentTier = (tierId: number) => {
    return currentSubscription?.subscription_tier_id === tierId;
  };

  const isUpgrade = (tier: SubscriptionTier) => {
    if (!currentSubscription) return false;
    const currentTier = tiers.find((t) => t.id === currentSubscription.subscription_tier_id);
    if (!currentTier) return false;
    return tier.pricing.monthly_price > currentTier.pricing.monthly_price;
  };

  const canSelectTier = (tier: SubscriptionTier) => {
    if (isCurrentTier(tier.id)) return false;
    return isUpgrade(tier);
  };

  const getStatusBadge = () => {
    if (!currentSubscription) return null;

    const { status, trial_end, cancel_at_period_end } = currentSubscription;

    if (status === 'trialing' && trial_end) {
      return (
        <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full">
          <AlertCircle className="w-3 h-3" />
          Trial ends {formatDate(trial_end)}
        </span>
      );
    }

    if (status === 'past_due') {
      return (
        <span className="inline-flex items-center gap-1 text-xs text-red-700 bg-red-50 px-2 py-0.5 rounded-full">
          <AlertCircle className="w-3 h-3" />
          Past due
        </span>
      );
    }

    if (cancel_at_period_end) {
      return (
        <span className="inline-flex items-center gap-1 text-xs text-gray-600 bg-gray-100 px-2 py-0.5 rounded-full">
          <AlertCircle className="w-3 h-3" />
          Cancels at period end
        </span>
      );
    }

    return null;
  };

  const formatLimitKey = (key: string) => {
    return key
      .replace(/_/g, ' ')
      .replace('max ', '')
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  if (loadingData) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} title="Change Plan" size="lg">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-avi-primary" />
        </div>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Change Plan" size="lg">
      <div className="space-y-4">
        {/* Current Plan Summary */}
        {currentSubscription && (() => {
          const currentTier = tiers.find((t) => t.id === currentSubscription.subscription_tier_id);
          const currentPrice = currentTier
            ? currentSubscription.billing_cycle === 'monthly'
              ? currentTier.pricing.monthly_price
              : currentTier.pricing.annual_price
            : currentSubscription.total_amount;

          return (
            <div className="bg-gray-50 rounded-md px-3 py-2.5 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">Current Plan</p>
                  <p className="text-sm font-medium text-gray-900">
                    {currentSubscription.tier_display_name}
                    <span className="text-gray-400 font-normal ml-1">
                      ({currentSubscription.billing_cycle === 'monthly' ? 'Monthly' : 'Annual'})
                    </span>
                  </p>
                </div>
              </div>
              <div className="text-right">
                <p className="text-sm font-semibold text-gray-900">
                  {formatPrice(currentPrice)}
                  <span className="text-xs text-gray-400 font-normal">
                    /{currentSubscription.billing_cycle === 'monthly' ? 'mo' : 'yr'}
                  </span>
                </p>
                <p className="text-xs text-gray-400">
                  Renews {formatDate(currentSubscription.current_period_end)}
                </p>
              </div>
            </div>
          );
        })()}

        {/* Status Badge */}
        {getStatusBadge() && (
          <div className="flex justify-center">
            {getStatusBadge()}
          </div>
        )}

        {/* Billing Cycle Toggle */}
        <div className="flex justify-center">
          <div className="inline-flex bg-gray-100 rounded-md p-0.5">
            <button
              onClick={() => setBillingCycle('monthly')}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-all ${
                billingCycle === 'monthly'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Monthly
            </button>
            <button
              onClick={() => setBillingCycle('annual')}
              className={`px-3 py-1.5 text-xs font-medium rounded transition-all ${
                billingCycle === 'annual'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Annual
              <span className="ml-1 text-[10px] text-green-600 font-semibold">-17%</span>
            </button>
          </div>
        </div>

        {/* Tier Selection */}
        <div className="grid grid-cols-3 gap-2">
          {tiers.map((tier) => {
            const price =
              billingCycle === 'monthly'
                ? tier.pricing.monthly_price
                : tier.pricing.annual_price;
            const isCurrent = isCurrentTier(tier.id);
            const canSelect = canSelectTier(tier);
            const isSelected = selectedTierId === tier.id;
            const isDowngrade = !isCurrent && !isUpgrade(tier);

            return (
              <div
                key={tier.id}
                onClick={() => canSelect && setSelectedTierId(tier.id)}
                className={`relative rounded-md border p-3 transition-all ${
                  isCurrent
                    ? 'border-avi-primary bg-avi-primary/5'
                    : isSelected
                    ? 'border-avi-primary bg-white shadow-sm ring-1 ring-avi-primary cursor-pointer'
                    : canSelect
                    ? 'border-gray-200 bg-white hover:border-gray-300 hover:shadow-sm cursor-pointer'
                    : 'border-gray-100 bg-gray-50/50 opacity-50 cursor-not-allowed'
                }`}
              >
                {/* Current Badge */}
                {isCurrent && (
                  <div className="absolute -top-2 left-1/2 -translate-x-1/2">
                    <span className="bg-avi-primary text-white text-[10px] font-medium px-1.5 py-0.5 rounded">
                      Current
                    </span>
                  </div>
                )}

                {/* Selected Indicator */}
                {isSelected && (
                  <div className="absolute top-1.5 right-1.5">
                    <div className="w-4 h-4 bg-avi-primary rounded-full flex items-center justify-center">
                      <Check className="w-2.5 h-2.5 text-white" strokeWidth={3} />
                    </div>
                  </div>
                )}

                <div className="text-center">
                  <h3 className="text-sm font-semibold text-gray-900">
                    {tier.tier_display_name}
                  </h3>
                  <div className="mt-1">
                    <span className="text-xl font-bold text-gray-900">
                      {formatPrice(price)}
                    </span>
                    <span className="text-xs text-gray-400">
                      /{billingCycle === 'monthly' ? 'mo' : 'yr'}
                    </span>
                  </div>

                  {/* Key Limits */}
                  <div className="mt-2 pt-2 border-t border-gray-100 space-y-1">
                    {Object.entries(tier.limits).slice(0, 3).map(([key, value]) => (
                      <div key={key} className="flex items-center justify-center gap-1 text-[11px]">
                        <Check className="w-3 h-3 text-green-500 flex-shrink-0" />
                        <span className="text-gray-600">
                          {value === -1 ? 'Unlimited' : value} {formatLimitKey(key)}
                        </span>
                      </div>
                    ))}
                  </div>

                  {/* Downgrade Notice */}
                  {isDowngrade && !isCurrent && (
                    <p className="mt-2 text-[10px] text-gray-400">
                      Downgrades coming soon
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Upgrade Preview */}
        {preview && (
          <div className="bg-gradient-to-r from-avi-primary/5 to-indigo-50 rounded-md p-3 border border-avi-primary/10">
            <div className="flex items-center gap-1.5 mb-2">
              <Sparkles className="w-3.5 h-3.5 text-avi-primary" />
              <h4 className="text-xs font-semibold text-gray-900">Upgrade Summary</h4>
            </div>
            <div className="grid grid-cols-4 gap-2 text-xs">
              <div>
                <p className="text-gray-500 text-[10px]">New Plan</p>
                <p className="font-medium text-gray-900">{preview.newTier}</p>
              </div>
              <div>
                <p className="text-gray-500 text-[10px]">Due Today</p>
                <p className="font-medium text-gray-900">{formatPrice(preview.proratedAmount)}</p>
              </div>
              <div>
                <p className="text-gray-500 text-[10px]">New Price</p>
                <p className="font-medium text-gray-900">
                  {formatPrice(preview.newPrice)}/{preview.billingCycle === 'monthly' ? 'mo' : 'yr'}
                </p>
              </div>
              <div>
                <p className="text-gray-500 text-[10px]">Next Billing</p>
                <p className="font-medium text-gray-900">{formatDate(preview.nextBillingDate)}</p>
              </div>
            </div>
          </div>
        )}

        {loadingPreview && (
          <div className="flex items-center justify-center py-2">
            <Loader2 className="w-4 h-4 animate-spin text-avi-primary mr-1.5" />
            <span className="text-xs text-gray-500">Calculating...</span>
          </div>
        )}

        {/* Past Due Warning */}
        {currentSubscription?.status === 'past_due' && (
          <div className="bg-red-50 border border-red-100 rounded-md p-2.5">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-xs font-medium text-red-800">Payment Required</p>
                <p className="text-[11px] text-red-600">
                  Update your payment method before upgrading.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-2 pt-3 border-t border-gray-100">
          <Button variant="outline" size="sm" onClick={onClose} disabled={upgrading}>
            Cancel
          </Button>
          <Button
            variant="primary"
            size="sm"
            onClick={handleUpgrade}
            disabled={!selectedTierId || upgrading || currentSubscription?.status === 'past_due'}
          >
            {upgrading ? (
              <>
                <Loader2 className="w-3 h-3 animate-spin mr-1.5" />
                Upgrading...
              </>
            ) : (
              'Upgrade Now'
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default UpgradeSubscriptionModal;
